import { createSelector } from "reselect";

import type {
  FiCatalogResponse,
  FiFocusArea,
} from "@mapmycustomers/shared/types/entity/fieldInsight";

import { getOrganizationId } from "@app/store/iam/selectors";
import { RootState } from "@app/store/rootReducer";

const EMPTY_CHARTS: FiCatalogResponse[] = [];
const EMPTY_FOCUS_AREAS: FiFocusArea[] = [];

const fieldInsightsState = (state: RootState) => state.fieldInsights;

// The catalog outlives an org switch in the store, so charts are only served to their own org.
export const getFieldInsightsCharts = createSelector(
  fieldInsightsState,
  getOrganizationId,
  ({ charts, chartsOrganizationId }, organizationId) =>
    chartsOrganizationId === organizationId ? charts : EMPTY_CHARTS
);

export const getFieldInsightsChartsScope = (state: RootState) =>
  fieldInsightsState(state).chartsScope;

export const didFieldInsightsChartsFail = (state: RootState) =>
  fieldInsightsState(state).chartsFailed;

export const isFieldInsightsChartsLoading = (state: RootState) =>
  fieldInsightsState(state).chartsLoading;

// Focus areas are org-scoped too, so they are only served back to their own org.
export const getFieldInsightsFocusAreas = createSelector(
  fieldInsightsState,
  getOrganizationId,
  ({ focusAreas, focusAreasOrganizationId }, organizationId) =>
    focusAreasOrganizationId === organizationId ? focusAreas : EMPTY_FOCUS_AREAS
);

// Served only to their own org, like the catalog and the focus areas above.
export const getFieldInsightsLimits = createSelector(
  fieldInsightsState,
  getOrganizationId,
  ({ limits, limitsOrganizationId }, organizationId) =>
    limitsOrganizationId === organizationId ? limits : undefined
);

export const didFieldInsightsLimitsFail = (state: RootState) =>
  fieldInsightsState(state).limitsFailed;

export const isFieldInsightsLimitsLoading = (state: RootState) =>
  fieldInsightsState(state).limitsLoading;

export const didFieldInsightsFocusAreasFail = (state: RootState) =>
  fieldInsightsState(state).focusAreasFailed;

export const isFieldInsightsFocusAreasLoading = (state: RootState) =>
  fieldInsightsState(state).focusAreasLoading;
